import React from 'react';
import { TableRow, TableCell, Checkbox, IconButton } from '@mui/material';
import { GLYPHS } from '@/components/ui';
import type { Employee } from '../../types/employee';
import { EmployeeEditableCell } from './EmployeeEditableCell';
import type { EmployeeSpreadsheetCellField, EditingCell } from './types';
const CELL_STYLE = {
  borderRight: '1px solid',
  borderColor: 'divider',
  py: 0,
  px: 0.5,
  verticalAlign: 'middle',
  overflow: 'hidden',
} as const;

interface EmployeeDataRowProps {
  employee: Employee;
  severanceMessage: string | null;
  isSelected: boolean;
  onSelect: () => void;
  editingCell: EditingCell | null;
  onStartEdit: (field: EmployeeSpreadsheetCellField) => void;
  onSave: (field: EmployeeSpreadsheetCellField, value: string | number | boolean) => void;
  onCancelEdit: () => void;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
}

export const EmployeeDataRow: React.FC<EmployeeDataRowProps> = ({
  employee,
  severanceMessage,
  isSelected,
  onSelect,
  editingCell,
  onStartEdit,
  onSave,
  onCancelEdit,
  onMenuOpen,
}) => {
  const isEditing = (field: EmployeeSpreadsheetCellField) =>
    editingCell?.type === 'data' &&
    editingCell.employeeId === employee.id &&
    editingCell.field === field;

  const hireDateValue = employee.hire_date || employee.hireDate;
  const wageValue = employee.isHourly ? employee.hourlyRate : employee.monthlySalary;
  const wageField: EmployeeSpreadsheetCellField = employee.isHourly ? 'hourlyRate' : 'monthlySalary';

  return (
    <TableRow hover selected={isSelected}>
      <TableCell
        padding="checkbox"
        sx={{
          ...CELL_STYLE,
          width: 48,
          minWidth: 48,
          maxWidth: 48,
          textAlign: 'center',
          p: 0,
          borderRight: 'none',
          overflow: 'visible',
        }}
      >
        <Checkbox checked={isSelected} onChange={onSelect} size="small" />
      </TableCell>
      <TableCell sx={CELL_STYLE}>
        <EmployeeEditableCell
          field="name"
          value={employee.name}
          isEditing={isEditing('name')}
          onStartEdit={() => onStartEdit('name')}
          onSave={(v) => onSave('name', v)}
          onCancel={onCancelEdit}
        />
      </TableCell>
      <TableCell sx={CELL_STYLE}>
        <EmployeeEditableCell
          field="hire_date"
          value={hireDateValue}
          isEditing={isEditing('hire_date')}
          onStartEdit={() => onStartEdit('hire_date')}
          onSave={(v) => onSave('hire_date', v)}
          onCancel={onCancelEdit}
        />
      </TableCell>
      <TableCell sx={CELL_STYLE}>
        <EmployeeEditableCell
          field="isHourly"
          value={employee.isHourly}
          isEditing={isEditing('isHourly')}
          onStartEdit={() => onStartEdit('isHourly')}
          onSave={(v) => onSave('isHourly', v)}
          onCancel={onCancelEdit}
        />
      </TableCell>
      <TableCell sx={{ ...CELL_STYLE, textAlign: 'right' }}>
        <EmployeeEditableCell
          field={wageField}
          value={wageValue}
          isEditing={isEditing(wageField)}
          onStartEdit={() => onStartEdit(wageField)}
          onSave={(v) => onSave(wageField, v as number)}
          onCancel={onCancelEdit}
          align="right"
        />
      </TableCell>
      <TableCell sx={CELL_STYLE}>
        <EmployeeEditableCell
          field="payday"
          value={employee.payday}
          isEditing={isEditing('payday')}
          onStartEdit={() => onStartEdit('payday')}
          onSave={(v) => onSave('payday', v)}
          onCancel={onCancelEdit}
        />
      </TableCell>
      <TableCell sx={CELL_STYLE}>
        <EmployeeEditableCell
          field="dependents"
          value={employee.dependents ?? 0}
          isEditing={isEditing('dependents')}
          onStartEdit={() => onStartEdit('dependents')}
          onSave={(v) => onSave('dependents', v)}
          onCancel={onCancelEdit}
        />
      </TableCell>
      <TableCell sx={CELL_STYLE}>
        <EmployeeEditableCell
          field="severance"
          value=""
          severanceMessage={severanceMessage}
          readOnly
          isEditing={false}
          onStartEdit={() => {}}
          onSave={() => {}}
          onCancel={onCancelEdit}
        />
      </TableCell>
      <TableCell sx={CELL_STYLE}>
        <EmployeeEditableCell
          field="isRetired"
          value={employee.isRetired ?? false}
          readOnly
          isEditing={false}
          onStartEdit={() => {}}
          onSave={() => {}}
          onCancel={onCancelEdit}
        />
      </TableCell>
      <TableCell sx={CELL_STYLE} padding="checkbox">
        <IconButton size="small" aria-label="더보기" onClick={onMenuOpen}
          sx={{ fontSize: 22, lineHeight: 1, color: 'var(--fg-secondary)' }}>
          {GLYPHS.more}
        </IconButton>
      </TableCell>
    </TableRow>
  );
};
